/**
 * RestTimer — Countdown between exercises in a routine.
 * Shows circular progress, next exercise preview, and skip button.
 */

import { useState, useEffect } from 'react';

interface RestTimerProps {
  seconds: number;
  nextExercise: string;
  targetReps?: number | null;
  onComplete: () => void;
  onSkip: () => void;
}

export default function RestTimer({ seconds, nextExercise, targetReps, onComplete, onSkip }: RestTimerProps) {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    if (remaining <= 0) {
      onComplete();
      return;
    }

    const timer = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(timer);
  }, [remaining, onComplete]);

  const progress = 1 - remaining / seconds;
  const circumference = 2 * Math.PI * 60; // radius 60
  const dashOffset = circumference * (1 - progress);

  return (
    <div className="flex flex-col items-center justify-center gap-6 py-8">
      {/* Circular countdown */}
      <div className="relative flex items-center justify-center">
        <svg width="160" height="160" className="-rotate-90">
          {/* Background circle */}
          <circle cx="80" cy="80" r="60" fill="none" stroke="#e5e7eb" strokeWidth="6" />
          {/* Progress circle */}
          <circle
            cx="80" cy="80" r="60"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            className="transition-all duration-1000 ease-linear"
          />
        </svg>
        <div className="absolute text-center">
          <span className="text-4xl font-black text-gray-900 tabular-nums">{remaining}</span>
          <p className="text-xs text-gray-400">seconds</p>
        </div>
      </div>

      {/* Next exercise preview */}
      <div className="text-center">
        <p className="text-sm text-gray-500">Up next</p>
        <p className="text-xl font-bold text-gray-900">{nextExercise}</p>
        {targetReps && (
          <p className="text-sm text-blue-600 font-medium">Target: {targetReps} reps</p>
        )}
      </div>

      {/* Skip button */}
      <button
        type="button"
        onClick={onSkip}
        className="rounded-xl bg-gray-100 px-6 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200"
      >
        Skip Rest
      </button>

      {/* Countdown beep at 3-2-1 */}
      {remaining <= 3 && remaining > 0 && (
        <p className="text-lg font-bold text-blue-600 animate-pulse">Get ready!</p>
      )}
    </div>
  );
}
